<template>
  <template v-if="viewingAsClient">
    <WdsAlert
      v-if="approvalWarning && approvalState"
      class="tw-mb-4"
      icon
      :error="approvalState.state === ApprovalApprovableState.Rejected"
      :warning="approvalState.state !== ApprovalApprovableState.Rejected"
      :title="approvalWarning"
    />

    <WdsAlert
      v-if="!approvalWarning && hire?.activeStatus === HireActiveStatus.Draft"
      info
      icon
      bordered
      class="tw-mb-3"
    >
      <WdsStack direction-row gap-sm justify-space-between>
        <div>
          {{ draftVisibilityMessage }}
          <span class="tw-text-black tw-pl-0.5">
            {{ $t('You can share the offer when all required steps are done.') }}
          </span>
        </div>
        <WdsButtonText :disabled="!canShare && !approvalWarning" @click="showShareHireModal = true">
          {{ $t('Finalise offer') }}
        </WdsButtonText>
      </WdsStack>
    </WdsAlert>

    <WdsAlert v-if="reclassificationRequired" error icon bordered class="tw-mb-3">
      <WdsStack justify-space-between>
        <span>
          {{ $t('Re-classification required after contract edits.') }}
        </span>
        <WdsButtonText @click="emit('switchTab', 'classification')">
          {{ $t('Go to') }}
        </WdsButtonText>
      </WdsStack>
    </WdsAlert>

    <!-- hasSignedScheduledChange keeps the banner up in the gap between the
         effective date arriving and the daily activation job running, when
         hasScheduledChanges (future-dated only) has already gone false. -->
    <WdsAlert
      v-if="hire?.hasScheduledChanges || hire?.hasSignedScheduledChange"
      info
      icon
      bordered
      class="tw-mb-3"
    >
      {{ scheduledChangesText }}
    </WdsAlert>

    <ShareHireModal
      :open="showShareHireModal"
      :hire-id="hireId"
      @close="showShareHireModal = false"
    />
  </template>

  <template v-else-if="hasWorkerCapabilities || viewerIsSupplier">
    <WdsAlert v-if="hire?.hasSignedScheduledChange" info icon bordered class="tw-mb-3">
      {{ scheduledChangesText }}
    </WdsAlert>
  </template>
</template>

<script setup lang="ts">
import { WdsAlert, WdsButtonText, WdsStack } from '@worksome/wds-components'
import { computed, ref } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import { useComplianceGate } from './HirePageCompliance/useComplianceGates'
import { useHireRecruiter } from './composables/useHireRecruiter'
import {
  ApprovalApprovableState,
  ApprovalTrigger,
  ComplianceName,
  HireActiveStatus,
} from '@/gql/graphql'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { graphql, useFragment } from '@/gql'
import ShareHireModal from '@/components/Hire/ShareHireModal.vue'
import { useAccountCapabilities } from '@/composables/useAccountCapabilities'
import useDate from '@/composables/useDate'

const { hasCompanyCapabilities, hasWorkerCapabilities } = useAccountCapabilities()

const props = defineProps<{
  hireId: string
}>()

const emit = defineEmits<{
  (e: 'switchTab', tab: string): void
}>()

const showShareHireModal = ref<boolean>(false)

// Use the new composable for recruiter information
const { hasRecruiterManagingWorkers, viewerIsSupplier } = useHireRecruiter(props.hireId)

// Mirrors HirePageHeader's `viewingAsClient`: a company acting as supplier on
// this hire sees the worker-perspective alerts, not the company-only ones.
const viewingAsClient = computed(() => hasCompanyCapabilities.value && !viewerIsSupplier.value)

const HireFragment = graphql(`
  fragment HireAlertFields on Hire {
    id
    activeStatus
    workerInvited
    activeContract {
      id
    }
    draftContract {
      id
      effectiveAt
    }
    hasScheduledChanges
    hasSignedScheduledChange
    hireChangedState: currentApprovalState(trigger: HIRE_CHANGED) {
      id
      approvalApprovable {
        id
        approval {
          id
          trigger
        }
      }
      actionedBy {
        id
        name
      }
      createdAt
      state
    }
    hireCreatedState: currentApprovalState(trigger: HIRE_CREATED) {
      id
      approvalApprovable {
        id
        approval {
          id
          trigger
        }
      }
      actionedBy {
        id
        name
      }
      createdAt
      state
    }
    usesClassification
    classification {
      id
      isReclassificationRequired
    }
  }
`)

const GET_HIRE = graphql(`
  query GetHireForHeaderAlerts($id: ID!) {
    hire(id: $id) {
      ...HireAlertFields
    }
  }
`)

const { result } = useQuery(GET_HIRE, () => ({ id: props.hireId }), {
  clientId: 'publicApi',
})

const hire = computed(() => useFragment(HireFragment, result.value?.hire))

const draftVisibilityMessage = computed(() => {
  if (hire.value?.workerInvited) {
    return hasRecruiterManagingWorkers.value
      ? $t('This hire is in draft. The staffing agency can see it to answer your questions, but not the offer.')
      : $t('This hire is in draft. The worker can see it to answer your questions, but not the offer.')
  }

  return hasRecruiterManagingWorkers.value
    ? $t('This hire is in draft and not visible to the staffing agency.')
    : $t('This hire is in draft and not visible to the worker.')
})

const approvalState = computed(() =>
  hire.value?.activeContract?.id ? hire.value?.hireChangedState : hire.value?.hireCreatedState,
)

const approvalWarning = computed(() => {
  const overallApprovalState = approvalState.value

  if (
    !overallApprovalState ||
    overallApprovalState.state === ApprovalApprovableState.Approved ||
    (overallApprovalState.approvalApprovable.approval.trigger === ApprovalTrigger.HireChanged &&
      overallApprovalState.state === ApprovalApprovableState.Rejected)
  ) {
    return null
  }

  switch (overallApprovalState.state) {
    case ApprovalApprovableState.Requested:
      return $t('Internal approval needed')
    case ApprovalApprovableState.Rejected:
      return $t('Approval denied by :user on :date', {
        user: overallApprovalState.actionedBy.name,
        date: overallApprovalState.createdAt,
      })
    default:
      return null
  }
})

const { completed } = useComplianceGate(props.hireId, ComplianceName.GateHireOffer)

const canShare = computed(() => completed.value)

const reclassificationRequired = computed(
  () => hire.value?.classification?.isReclassificationRequired && hire.value?.usesClassification,
)

const draftEffectiveAt = computed(() => hire.value?.draftContract?.effectiveAt ?? null)
const { date: draftEffectiveDateFormatted, isValid: draftEffectiveDateIsValid } = useDate(
  draftEffectiveAt,
  'MMM D, YYYY',
)

const scheduledEffectiveDate = computed(() =>
  draftEffectiveDateIsValid.value ? draftEffectiveDateFormatted.value : '',
)

const scheduledChangesText = computed(() => {
  const date = scheduledEffectiveDate.value

  if (viewingAsClient.value) {
    return hire.value?.hasSignedScheduledChange
      ? $t('The worker has signed contract changes that take effect on :date.', { date })
      : $t(
          'Contract changes are scheduled to take effect on :date, once the worker accepts them.',
          { date },
        )
  }

  return $t('Contract changes are scheduled to take effect on :date.', { date })
})
</script>
